import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Card, CardContent, CardHeader, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, Divider, GridLegacy as Grid, IconButton, Skeleton, Stack, Typography, Button } from '@mui/material';
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import { AxiosInstance, getUrlImage } from "../utils/constants";

const CABECERA_LABEL = "Cabecera";
const IMAGE_THUMB_SIZE = 300;

const cardSx = {
    borderRadius: { xs: 2, sm: 3 },
    boxShadow: { xs: 1, sm: 2 },
    overflow: "hidden",
    mb: { xs: 2, sm: 2.5 },
};

const getItemsByClase = (fichaTecnica, claseCod) =>
    fichaTecnica
        .filter((ele) => ele.clase_cod === claseCod)
        .sort((a, b) => a.atributo_orden - b.atributo_orden);

const imageThumbBoxSx = {
    width: IMAGE_THUMB_SIZE,
    height: IMAGE_THUMB_SIZE,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: "grey.100",
    borderRadius: 2,
    border: "1px solid",
    borderColor: "divider",
    overflow: "hidden",
    mx: "auto",
    position: "relative",
    flexShrink: 0,
};

const FichaImagen = ({ element }) => {
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const imageUrl = element.valor ? getUrlImage(element.valor) : null;
    const altText = element.atributo_descripcion || "Imagen del producto";

    const openLightbox = () => {
        if (imageUrl) setLightboxOpen(true);
    };

    if (!imageUrl) {
        return (
            <Box sx={{ ...imageThumbBoxSx, flexDirection: "column", gap: 1, color: "text.secondary" }}>
                <ImageNotSupportedOutlinedIcon sx={{ fontSize: 40, opacity: 0.5 }} />
                <Typography variant="caption">Sin imagen</Typography>
            </Box>
        );
    }

    return (
        <>
            <Box
                role="button"
                tabIndex={0}
                aria-label={`Ver imagen ampliada: ${altText}`}
                onClick={openLightbox}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openLightbox();
                    }
                }}
                sx={{
                    ...imageThumbBoxSx,
                    cursor: "pointer",
                    transition: "box-shadow 0.2s, transform 0.2s",
                    "&:hover": {
                        boxShadow: 3,
                        "& .ficha-imagen-zoom-hint": { opacity: 1 },
                    },
                    "&:focus-visible": {
                        outline: "2px solid",
                        outlineColor: "primary.main",
                        outlineOffset: 2,
                    },
                }}
            >
                <Box
                    component="img"
                    src={imageUrl}
                    alt={altText}
                    sx={{
                        width: IMAGE_THUMB_SIZE,
                        height: IMAGE_THUMB_SIZE,
                        objectFit: "contain",
                        objectPosition: "center",
                        display: "block",
                    }}
                />
                <Box
                    className="ficha-imagen-zoom-hint"
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(0,0,0,0.35)",
                        opacity: 0,
                        transition: "opacity 0.2s",
                        color: "common.white",
                    }}
                >
                    <ZoomInIcon sx={{ fontSize: 48 }} />
                </Box>
            </Box>

            <Dialog
                open={lightboxOpen}
                onClose={() => setLightboxOpen(false)}
                fullScreen
                PaperProps={{ sx: { bgcolor: "rgba(0,0,0,0.92)" } }}
            >
                <IconButton
                    aria-label="Cerrar imagen ampliada"
                    onClick={() => setLightboxOpen(false)}
                    sx={{
                        position: "absolute",
                        top: { xs: 8, sm: 16 },
                        right: { xs: 8, sm: 16 },
                        color: "common.white",
                        bgcolor: "rgba(255,255,255,0.12)",
                        zIndex: 1,
                        "&:hover": { bgcolor: "rgba(255,255,255,0.24)" },
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <Box
                    onClick={() => setLightboxOpen(false)}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        height: "100%",
                        p: { xs: 2, sm: 4 },
                        boxSizing: "border-box",
                        cursor: "zoom-out",
                    }}
                >
                    <Box
                        component="img"
                        src={imageUrl}
                        alt={altText}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            width: "auto",
                            height: "auto",
                            objectFit: "contain",
                            objectPosition: "center",
                        }}
                    />
                </Box>
            </Dialog>
        </>
    );
};

const FichaTexto = ({ element }) => {
    if (!element.valor) return null;

    return (
        <Grid item xs={12} sm={6}>
            <Box
                sx={{
                    py: 1.25,
                    px: { xs: 0, sm: 0.5 },
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    height: "100%",
                }}
            >
                <Typography
                    variant="caption"
                    color="text.secondary"
                    component="div"
                    sx={{ fontWeight: 600, letterSpacing: 0.2, textTransform: "uppercase", mb: 0.5 }}
                >
                    {element.atributo_descripcion}
                </Typography>
                <Typography variant="body1" color="text.primary" sx={{ lineHeight: 1.5, wordBreak: "break-word" }}>
                    {element.valor}
                </Typography>
            </Box>
        </Grid>
    );
};

const LoadingSkeleton = () => (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}>
        <Skeleton variant="rounded" height={220} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={120} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={180} sx={{ borderRadius: 2 }} />
    </Container>
);

export const FichaTecnicaView = ({ cod_producto, cod_usuario, embedded = false }) => {
    const [isLoading, setIsLoading] = useState("CARGANDO");
    const [fichaTecnica, setFichaTecnica] = useState([]);

    const Clases = useMemo(() => {
        const arrayOfClases = [];
        (fichaTecnica || []).forEach((item) => {
            if (!arrayOfClases.find((ele) => ele.cod === item.clase_cod)) {
                arrayOfClases.push({
                    cod: item.clase_cod,
                    label: item.clase_descripcion,
                });
            }
        });
        return arrayOfClases;
    }, [fichaTecnica]);

    const claseCabecera = Clases.find((c) => c.label === CABECERA_LABEL);
    const clasesContenido = Clases.filter((c) => c.label !== CABECERA_LABEL);

    useEffect(() => {
        if (!cod_producto) return;

        setIsLoading("CARGANDO");
        AxiosInstance.get(
            `maestros/obtener-ficha-tecnica-public/${btoa(String(cod_producto))}/${btoa(String(cod_usuario))}`
        )
            .then(({ data }) => {
                if (data.status) {
                    setIsLoading("LOADED");
                    setFichaTecnica(data.value);
                } else {
                    setIsLoading("WITH_ERROR");
                }
            })
            .catch(() => {
                setIsLoading("WITH_ERROR");
            });
    }, [cod_producto, cod_usuario]);

    const renderClaseItems = (claseItem) => {
        const items = getItemsByClase(fichaTecnica, claseItem.cod);
        const imagenes = items.filter((el) => el.atributo_tipo_atributo === "IMAGEN");
        const textos = items.filter((el) => el.atributo_tipo_atributo === "TEXTO");

        return (
            <>
                {imagenes.length > 0 && (
                    <Stack
                        direction="row"
                        flexWrap="wrap"
                        useFlexGap
                        spacing={2}
                        justifyContent="center"
                        sx={{ mb: textos.length ? 2 : 0 }}
                    >
                        {imagenes.map((element) => (
                            <FichaImagen key={element.atributo_codigo} element={element} />
                        ))}
                    </Stack>
                )}
                {textos.length > 0 && (
                    <Grid container spacing={{ xs: 0, sm: 1 }}>
                        {textos.map((element) => (
                            <FichaTexto key={element.atributo_codigo} element={element} />
                        ))}
                    </Grid>
                )}
            </>
        );
    };

    const pageContent = () => {
        if (isLoading === "CARGANDO") return <LoadingSkeleton />;

        if (isLoading === "WITH_ERROR") {
            return (
                <Container maxWidth="sm" sx={{ py: 4, px: 2 }}>
                    <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
                        No se pudo cargar la ficha técnica. Verifica el enlace o intenta más tarde.
                    </Alert>
                </Container>
            );
        }

        const itemsCabecera = claseCabecera ? getItemsByClase(fichaTecnica, claseCabecera.cod) : [];
        const hasCabeceraContent = itemsCabecera.some(
            (el) => el.valor && (el.atributo_tipo_atributo === "IMAGEN" || el.atributo_tipo_atributo === "TEXTO")
        );

        return (
            <Container
                maxWidth="md"
                disableGutters={embedded}
                sx={{
                    py: embedded ? { xs: 1, sm: 1.5 } : { xs: 2, sm: 3, md: 4 },
                    px: embedded ? { xs: 0, sm: 0.5 } : { xs: 2, sm: 3 },
                }}
            >
                {!embedded && (
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: { xs: 2, sm: 3 } }}>
                        <DescriptionOutlinedIcon color="primary" sx={{ fontSize: { xs: 28, sm: 32 } }} />
                        <Box>
                            <Typography variant="h5" component="h1" sx={{ fontWeight: 700, fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
                                Ficha técnica
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Información del producto
                            </Typography>
                        </Box>
                    </Stack>
                )}

                {hasCabeceraContent && claseCabecera && (
                    <Card sx={cardSx} elevation={embedded ? 0 : undefined}>
                        <CardContent sx={{ p: { xs: 0, sm: 0 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
                            <Box sx={{ px: { xs: 0, sm: 0 }, pt: { xs: 0, sm: 0 } }}>
                                {renderClaseItems(claseCabecera)}
                            </Box>
                        </CardContent>
                    </Card>
                )}

                <Stack spacing={{ xs: 0, sm: 0 }}>
                    {clasesContenido.map((claseItem) => {
                        const items = getItemsByClase(fichaTecnica, claseItem.cod);
                        const hasVisibleContent = items.some(
                            (el) =>
                                el.valor &&
                                (el.atributo_tipo_atributo === "IMAGEN" || el.atributo_tipo_atributo === "TEXTO")
                        );
                        if (!hasVisibleContent) return null;

                        return (
                            <Card key={claseItem.cod} sx={cardSx} elevation={embedded ? 0 : undefined}>
                                <CardHeader
                                    title={claseItem.label}
                                    titleTypographyProps={{
                                        variant: "h6",
                                        fontWeight: 700,
                                        fontSize: { xs: "1rem", sm: "1.125rem" },
                                    }}
                                    sx={{
                                        bgcolor: "grey.50",
                                        borderBottom: 1,
                                        borderColor: "divider",
                                        py: { xs: 1.5, sm: 2 },
                                        px: { xs: 2, sm: 2.5 },
                                        "& .MuiCardHeader-title": { lineHeight: 1.3 },
                                    }}
                                />
                                <CardContent sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 2, sm: 2.5 } }}>
                                    {renderClaseItems(claseItem)}
                                </CardContent>
                            </Card>
                        );
                    })}
                </Stack>

                {clasesContenido.length === 0 && !hasCabeceraContent && (
                    <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                        Esta ficha técnica no tiene información publicada.
                    </Alert>
                )}
            </Container>
        );
    };

    if (embedded) {
        return pageContent();
    }

    return (
        <Box
            component="main"
            sx={{
                minHeight: "100vh",
                bgcolor: "grey.50",
                pb: { xs: 3, sm: 4 },
            }}
        >
            {isLoading === "CARGANDO" ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "60vh",
                    }}
                >
                    <CircularProgress color="primary" size={48} />
                </Box>
            ) : (
                pageContent()
            )}
        </Box>
    );
};

export const ModalFichaTecnicaView = ({ cod_producto, cod_usuario, is_opened, onClose }) => (
    <Dialog
        fullWidth
        maxWidth="md"
        open={is_opened}
        onClose={onClose}
        scroll="paper"
        PaperProps={{
            sx: {
                borderRadius: { xs: 0, sm: 3 },
                m: { xs: 0, sm: 2 },
                maxHeight: { xs: "100%", sm: "90vh" },
            },
        }}
    >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Ficha técnica del producto</DialogTitle>
        <Divider />
        <DialogContent sx={{ bgcolor: "grey.50", px: { xs: 2, sm: 3 }, py: 2 }}>
            <FichaTecnicaView cod_producto={cod_producto} cod_usuario={cod_usuario} embedded />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={onClose} variant="contained" color="primary" fullWidth sx={{ maxWidth: { sm: 200 } }}>
                Cerrar
            </Button>
        </DialogActions>
    </Dialog>
);
